/**
 * Task Queue + Task Progress
 *
 * TaskQueue — Legacy class used ONLY by onboarding orchestrator.
 *   Same old floating panel with addTask/updateTask/addViewFindings API.
 *
 * TaskProgress — NEW compact progress card (top-left) for real task execution.
 *   Shows a single card per plan: progress bar, current step, worker info.
 *   Transforms to completion card with "View Deliverable" button.
 *
 * Events consumed by TaskProgress:
 *   - 'plan-approved' → initialize card
 *   - WebSocket 'task-update' → update progress
 *   - 'plan-completed' → transform to completion state
 */

import { apiClient } from '@/lib/api-client';
import { dataManager } from '@/data/data-manager';
import { extractDeskIndex } from '@/utils/worker-utils';
import type { TaskRecord } from '@/data/types';

// ═══════════════════════════════════════════════════════════════
// TaskProgress — compact progress card for real task execution
// ═══════════════════════════════════════════════════════════════

// ── Simulated progress phases when no real statusText arrives ──
const TASK_PHASES: Record<string, string[]> = {
  research: ['Gathering sources...', 'Reading references...', 'Cross-referencing data...', 'Evaluating credibility...', 'Synthesizing findings...', 'Drafting report...', 'Reviewing conclusions...', 'Polishing final report...'],
  draft: ['Outlining structure...', 'Writing opening section...', 'Developing key points...', 'Adding supporting details...', 'Refining language...', 'Improving flow...', 'Polishing tone...', 'Final review...'],
  analyze: ['Collecting data points...', 'Running initial analysis...', 'Identifying patterns...', 'Comparing options...', 'Evaluating trade-offs...', 'Drawing conclusions...', 'Writing recommendations...', 'Finalizing analysis...'],
  plan: ['Identifying requirements...', 'Mapping dependencies...', 'Estimating timelines...', 'Setting milestones...', 'Assigning responsibilities...', 'Checking constraints...', 'Finalizing timeline...', 'Preparing deliverable...'],
  general: ['Getting started...', 'Processing request...', 'Building output...', 'Making progress...', 'Refining results...', 'Adding detail...', 'Almost there...', 'Wrapping up...'],
};

// ── Stuck detection thresholds ──
const STUCK_WARNING_MS = 30_000;  // 30s no real update
const CLIENT_TIMEOUT_MS = 120_000; // 2min no real update

interface TrackedTask {
  id: string;
  description: string;
  workerId: string;
  workerName: string;
  taskType: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  statusText?: string;
  lastStatusUpdate: number;   // timestamp of last real statusText
  simulatedPhase: number;     // index into TASK_PHASES
}

export class TaskProgress {
  private card: HTMLElement;
  private planId: string | null = null;
  private summary = '';
  private tasks: TrackedTask[] = [];
  private isComplete = false;
  private deliverableId: string | null = null;
  private wsCleanup: (() => void) | null = null;
  private elapsedStartTime: number | null = null;
  private elapsedTimer: ReturnType<typeof setInterval> | null = null;
  private simulatedTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    this.card = document.createElement('div');
    this.card.id = 'task-progress-card';
    document.body.appendChild(this.card);

    // Listen for plan approval
    window.addEventListener('plan-approved', ((e: CustomEvent) => {
      const detail = e.detail as {
        planId: string;
        summary?: string;
        tasks: Array<{
          id: string;
          worker_id: string;
          description: string;
          task_type: string;
          status: string;
        }>;
      };
      this.initFromPlan(detail);
    }) as EventListener);

    // Listen for WebSocket task updates
    this.wsCleanup = apiClient.onSSE('task-update', (event) => {
      const data = event.data as {
        taskId: string;
        workerId: string;
        description: string;
        taskType: string;
        status: string;
        deliverableId?: string;
        statusText?: string;
      };
      this.handleTaskUpdate(data);
    });

    // Listen for plan completion
    window.addEventListener('plan-completed', ((e: CustomEvent) => {
      const data = e.detail as {
        planId: string;
        deliverableId: string | null;
        totalTasks: number;
        completedTasks: number;
      };
      this.handlePlanCompleted(data);
    }) as EventListener);
  }

  private initFromPlan(detail: {
    planId: string;
    summary?: string;
    tasks: Array<{
      id: string;
      worker_id: string;
      description: string;
      task_type: string;
      status: string;
    }>;
  }): void {
    this.planId = detail.planId;
    this.summary = detail.summary || 'Task Plan';
    this.isComplete = false;
    this.deliverableId = null;

    this.tasks = detail.tasks.map(t => ({
      id: t.id,
      description: t.description,
      workerId: t.worker_id,
      workerName: this.resolveWorkerName(t.worker_id),
      taskType: t.task_type || 'general',
      status: (t.status as TrackedTask['status']) || 'pending',
      lastStatusUpdate: Date.now(),
      simulatedPhase: 0,
    }));

    this.startTimers();
    this.render();
    this.show();
  }

  private handleTaskUpdate(data: {
    taskId: string;
    workerId: string;
    description: string;
    taskType: string;
    status: string;
    deliverableId?: string;
    statusText?: string;
  }): void {
    const task = this.tasks.find(t => t.id === data.taskId);
    if (!task) return;

    task.status = data.status as TrackedTask['status'];
    if (data.statusText) {
      task.statusText = data.statusText;
      task.lastStatusUpdate = Date.now();
      task.simulatedPhase = 0; // Reset simulated phase on real update
    }

    // Start elapsed timer on first in_progress task
    if (data.status === 'in_progress' && !this.elapsedStartTime) {
      this.elapsedStartTime = Date.now();
    }

    this.render();
  }

  private handlePlanCompleted(data: {
    planId: string;
    deliverableId: string | null;
    totalTasks: number;
    completedTasks: number;
  }): void {
    if (this.planId && this.planId !== data.planId) return;

    this.isComplete = true;
    this.deliverableId = data.deliverableId;
    this.stopTimers();

    for (const t of this.tasks) {
      if (t.status === 'pending' || t.status === 'in_progress') {
        t.status = 'completed';
      }
    }

    this.render();

    setTimeout(() => {
      if (this.isComplete) {
        this.card.classList.add('fading');
        setTimeout(() => this.hide(), 500);
      }
    }, 30000);
  }

  private startTimers(): void {
    this.stopTimers();

    // Elapsed timer — update every second
    this.elapsedTimer = setInterval(() => {
      if (this.elapsedStartTime && !this.isComplete) {
        this.updateElapsed();
      }
    }, 1000);

    // Simulated progress — cycle fake phases every 5s for tasks with stale statusText
    this.simulatedTimer = setInterval(() => {
      if (this.isComplete) return;
      const now = Date.now();
      let changed = false;
      for (const task of this.tasks) {
        if (task.status !== 'in_progress') continue;
        const timeSinceUpdate = now - task.lastStatusUpdate;

        if (timeSinceUpdate > CLIENT_TIMEOUT_MS) {
          // 2 minutes with no real update — show timeout warning
          task.statusText = 'Taking a while. Task may still be running...';
          changed = true;
        } else if (timeSinceUpdate > STUCK_WARNING_MS) {
          // 30s with no real update — show stuck warning
          task.statusText = 'Taking longer than expected...';
          changed = true;
        } else if (timeSinceUpdate > 15000) {
          // No real update in 15s — advance simulated phase
          const phases = TASK_PHASES[task.taskType] || TASK_PHASES.general;
          task.simulatedPhase = (task.simulatedPhase + 1) % phases.length;
          task.statusText = `${task.workerName}: ${phases[task.simulatedPhase]}`;
          changed = true;
        }
      }
      if (changed) this.render();
    }, 5000);
  }

  private stopTimers(): void {
    if (this.elapsedTimer) { clearInterval(this.elapsedTimer); this.elapsedTimer = null; }
    if (this.simulatedTimer) { clearInterval(this.simulatedTimer); this.simulatedTimer = null; }
  }

  private updateElapsed(): void {
    const timerEl = this.card.querySelector('.tp-elapsed');
    if (!timerEl || !this.elapsedStartTime) return;
    const secs = Math.floor((Date.now() - this.elapsedStartTime) / 1000);
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    timerEl.textContent = `\u23F1\uFE0F ${m}m ${s.toString().padStart(2, '0')}s elapsed`;
  }

  private render(): void {
    const completedCount = this.tasks.filter(t => t.status === 'completed').length;
    const totalCount = this.tasks.length;
    const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

    if (this.isComplete) {
      this.renderComplete(completedCount, totalCount);
    } else {
      this.renderChecklist(completedCount, totalCount, progress);
    }
  }

  private renderChecklist(completed: number, total: number, progress: number): void {
    const rows = this.tasks.map(t => {
      let icon: string;
      let statusCls: string;
      let statusLabel: string;

      switch (t.status) {
        case 'completed':
          icon = '\u2705'; statusCls = 'done'; statusLabel = 'done';
          break;
        case 'in_progress':
          icon = '\u23F3'; statusCls = 'active';
          statusLabel = t.statusText || `${this.taskTypeVerb(t.taskType)}...`;
          break;
        case 'failed':
          icon = '\u274C'; statusCls = 'failed'; statusLabel = 'failed';
          break;
        default:
          icon = '\u23F8\uFE0F'; statusCls = 'pending'; statusLabel = 'pending';
          break;
      }

      const desc = this.truncate(t.description, 35);

      return `<div class="tp-row tp-row-${statusCls}">
        <span class="tp-row-icon">${icon}</span>
        <span class="tp-row-name">${this.escapeHtml(t.workerName)}</span>
        <span class="tp-row-desc">${this.escapeHtml(desc)}</span>
        <span class="tp-row-status">${this.escapeHtml(this.truncate(statusLabel, 25))}</span>
      </div>`;
    }).join('');

    const pct = Math.round(progress);

    // Elapsed timer text
    let elapsedText = '';
    if (this.elapsedStartTime) {
      const secs = Math.floor((Date.now() - this.elapsedStartTime) / 1000);
      const m = Math.floor(secs / 60);
      const s = secs % 60;
      elapsedText = `\u23F1\uFE0F ${m}m ${s.toString().padStart(2, '0')}s elapsed`;
    }

    this.card.innerHTML = `
      <div class="tp-header">
        <span class="tp-icon">\uD83D\uDCCB</span>
        <span class="tp-title">${this.escapeHtml(this.truncate(this.summary, 32))}</span>
      </div>
      <div class="tp-progress-row">
        <div class="tp-progress-bar">
          <div class="tp-progress-fill" style="width: ${pct}%"></div>
        </div>
        <span class="tp-step-label">${pct}%</span>
      </div>
      <div class="tp-checklist">${rows}</div>
      <div class="tp-elapsed">${elapsedText}</div>
    `;

    this.card.classList.remove('complete');
  }

  private renderComplete(completed: number, total: number): void {
    this.card.innerHTML = `
      <div class="tp-header">
        <span class="tp-icon tp-done-icon">\u2705</span>
        <span class="tp-title">${this.escapeHtml(this.truncate(this.summary, 32))}</span>
      </div>
      <div class="tp-complete-info">${completed}/${total} tasks complete</div>
      ${this.deliverableId ? `<button class="tp-view-btn">View Deliverable</button>` : ''}
    `;

    this.card.classList.add('complete');

    if (this.deliverableId) {
      const btn = this.card.querySelector('.tp-view-btn');
      btn?.addEventListener('click', () => this.viewDeliverable());
    }
  }

  private async viewDeliverable(): Promise<void> {
    if (!this.deliverableId) return;
    try {
      const deliverable = await dataManager.getDeliverableContent(this.deliverableId);
      if (deliverable?.content) {
        window.dispatchEvent(new CustomEvent('view-deliverable', {
          detail: deliverable,
        }));
      }
    } catch (err) {
      console.warn('Failed to load deliverable:', err);
    }
  }

  private taskTypeVerb(taskType: string): string {
    switch (taskType) {
      case 'research': return 'Researching';
      case 'analyze': return 'Analyzing';
      case 'draft': return 'Drafting';
      case 'plan': return 'Planning';
      default: return 'Working';
    }
  }

  private resolveWorkerName(workerId: string): string {
    if (dataManager.initialized) {
      const idx = extractDeskIndex(workerId);
      if (idx !== null) {
        return dataManager.getWorkerName(idx);
      }
    }
    return 'Worker';
  }

  // ── Staged Task Additions (for choreographed delegation) ──

  initEmpty(planId: string, summary: string): void {
    this.planId = planId;
    this.summary = summary || 'Task Plan';
    this.isComplete = false;
    this.deliverableId = null;
    this.elapsedStartTime = null;
    this.tasks = [];
    this.startTimers();
    this.render();
    this.show();
  }

  addTask(task: {
    id: string;
    worker_id: string;
    description: string;
    task_type: string;
  }): void {
    this.tasks.push({
      id: task.id,
      description: task.description,
      workerId: task.worker_id,
      workerName: this.resolveWorkerName(task.worker_id),
      taskType: task.task_type || 'general',
      status: 'pending',
      lastStatusUpdate: Date.now(),
      simulatedPhase: 0,
    });
    this.render();
  }

  show(): void {
    this.card.classList.add('visible');
    this.card.classList.remove('fading');
  }

  hide(): void {
    this.card.classList.remove('visible');
    this.stopTimers();
  }

  dispose(): void {
    this.stopTimers();
    this.wsCleanup?.();
    this.card.remove();
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private truncate(text: string, maxLen: number): string {
    if (text.length <= maxLen) return text;
    return text.slice(0, maxLen - 1) + '\u2026';
  }
}

// ═══════════════════════════════════════════════════════════════
// TaskQueue — Legacy class used ONLY by onboarding orchestrator
// ═══════════════════════════════════════════════════════════════

export class TaskQueue {
  private container: HTMLElement;
  private listEl: HTMLElement;
  private tasks: Map<string, { record: TaskRecord; el: HTMLElement }> = new Map();

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'task-queue-panel';
    this.container.innerHTML = `
      <div class="task-queue-header">Tasks</div>
      <div class="task-queue-list"></div>
    `;
    document.body.appendChild(this.container);
    this.listEl = this.container.querySelector('.task-queue-list')!;
  }

  // ── Legacy API (onboarding compatibility) ────────────────────

  /** Legacy: set deliverable modal for onboarding "View Findings" */
  setModal(_modal: unknown): void {
    // No-op: deliverables now use the view-deliverable event
  }

  /** Legacy: add a demo task by string ID (onboarding only) */
  addTask(id: string, description: string, workerName: string): void {
    const row = document.createElement('div');
    row.className = 'task-queue-item';
    row.innerHTML = `
      <div class="task-queue-item-top">
        <span class="task-queue-item-icon"><span class="task-queue-spinner"></span></span>
        <span class="task-queue-item-desc">${this.escapeHtml(description)}</span>
      </div>
      <div class="task-queue-item-meta">
        <span class="task-queue-item-worker">${this.escapeHtml(workerName)}</span>
        <span class="task-queue-item-status">In progress</span>
      </div>
    `;
    this.listEl.appendChild(row);
    this.tasks.set(id, {
      record: { id, status: 'in_progress', description, worker_id: '', user_id: '', task_type: null, parent_task_id: null, result_json: null, created_at: Date.now(), started_at: Date.now(), completed_at: null },
      el: row,
    });
  }

  /** Legacy: update a demo task status (onboarding only) */
  updateTask(id: string, status: 'complete'): void {
    const entry = this.tasks.get(id);
    if (!entry) return;
    if (status === 'complete') {
      entry.record.status = 'completed';
      entry.el.classList.add('complete');
      const iconEl = entry.el.querySelector('.task-queue-item-icon');
      if (iconEl) iconEl.innerHTML = '\u2705';
      const statusEl = entry.el.querySelector('.task-queue-item-status');
      if (statusEl) statusEl.textContent = 'Complete';
    }
  }

  /** Legacy: add a "View Findings" button (onboarding stock demo) */
  addViewFindings(id: string, stockData: unknown): void {
    const entry = this.tasks.get(id);
    if (!entry) return;
    const btn = document.createElement('button');
    btn.className = 'task-queue-view-btn';
    btn.textContent = '\uD83D\uDCCA View Findings';
    btn.addEventListener('click', () => {
      window.dispatchEvent(new CustomEvent('view-stock-findings', {
        detail: stockData,
      }));
    });
    entry.el.appendChild(btn);
  }

  show(): void {
    this.container.classList.add('visible');
  }

  hide(): void {
    this.container.classList.remove('visible');
  }

  dispose(): void {
    this.container.remove();
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
